import { blockedPopup, versionChangedPopup } from 'libs/DB/BaseDB'
import { davKey, toDavPath } from 'libs/dav/davPath'

interface ContactData {
  serverId: string
  etag: string
  emails: string
  phones: string
  allValues: string
  contact: any
}

interface AddressBookData {
  url: string
  syncToken?: string
  displayName?: string
  ctag?: string
  isSync: boolean
  isDefault: boolean
  folderType: string
}

interface PhotoData {
  serverId: string
  photo: string
}

interface PromiseResolveReject {
  resolve: (value: any) => void
  reject: (reason?: any) => void
}

type TableName = 'contact' | 'addressBook' | 'photo'

/**
 * host 가 다른 절대 URL 로 저장된 두 addressBook 레코드를 같은 collection 으로 병합한다.
 * (v3 마이그레이션에서 서버 host 변경으로 인해 만들어졌을 수 있는 중복 레코드를 정리하기 위함)
 * - isSync 는 둘 중 하나라도 true 면 true 로 유지한다 (사용자가 켠 동기화 상태를 잃지 않기 위함).
 * - 나머지 메타데이터는 다음 서버 fetch 에서 갱신되므로, 비어있지 않은 값을 우선한다.
 */
const mergeAddressBookRecords = (
  base: AddressBookData,
  other: AddressBookData,
): AddressBookData => ({
  url: base.url,
  syncToken: other.syncToken || base.syncToken,
  displayName: other.displayName || base.displayName,
  ctag: other.ctag || base.ctag,
  isSync: base.isSync || other.isSync,
  isDefault: base.isDefault || other.isDefault,
  folderType: other.folderType || base.folderType,
})

/**
 * addressBook 레코드 목록의 url 을 path 로 정규화하고, 같은 collection 을 가리키는
 * 레코드가 여럿이면 mergeAddressBookRecords 규칙으로 하나로 합친다.
 */
const migrateAddressBookRecordsToPath = (records: AddressBookData[]): AddressBookData[] => {
  const merged = new Map<string, AddressBookData>()

  records.forEach((record) => {
    const path = toDavPath(record.url)
    if (!path) {
      return
    }

    const key = davKey(path)
    const normalized: AddressBookData = { ...record, url: path }
    const existing = merged.get(key)
    merged.set(key, existing ? mergeAddressBookRecords(existing, normalized) : normalized)
  })

  return Array.from(merged.values())
}

class PersonalContactDB {
  isActive: boolean = false
  db: IDBDatabase | null = null
  loaded: boolean = false
  autoInit: boolean = true
  isInitialing: boolean = false
  initWaitingReq: PromiseResolveReject[] = []

  private dbName: string = 'personalContacts'
  private contactTableName: string = 'contact'
  private addressBookTableName: string = 'addressBook'
  private photoTableName: string = 'photo'
  // v2: vCard 구조화 값의 다중값 컴포넌트를 배열 그대로 저장하던 시기의 캐시를 폐기한다.
  // etag 가 같으면 재파싱하지 않으므로 버전을 올려 store 를 재생성해야 정상 값으로 다시 채워진다.
  // v3: addressBook 레코드의 키(url)를 host 포함 절대 URL 에서 path 로 정규화한다.
  // 서버 host 가 바뀌어도(carddav 이전 등) 같은 collection 으로 인식되도록 하기 위함이며,
  // contact/photo 캐시와 달리 addressBook 의 isSync 사용자 설정은 폐기하지 않고 이전한다.
  private version: number = 3

  constructor() {
    if (this.autoInit === true) {
      this.init()
    }
  }

  private init(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      if (this.isInitialing !== true) {
        this.isInitialing = true

        const DBOpenRequest = window.indexedDB.open(this.dbName, this.version)

        DBOpenRequest.onerror = (error) => {
          console.error('error - db open failed', error)
          this.isActive = false
        }

        DBOpenRequest.onsuccess = (event) => {
          console.log('success - db open success.')

          this.db = (event.target as IDBOpenDBRequest).result

          // 다른 탭이 새 버전으로 upgrade를 요청하면 이 연결을 닫아야 해당 탭의 open이 block되지 않는다.
          this.db.onversionchange = () => {
            console.warn('warning - db version changed by another tab.', this.dbName)
            this.db?.close()
            this.db = null
            this.isActive = false
            this.loaded = false
            versionChangedPopup()
          }

          this.isActive = true

          this.initWaitingReq.forEach((promise) => {
            promise.resolve(true)
          })
          this.initWaitingReq = []

          this.isInitialing = false
          resolve(true)
        }

        DBOpenRequest.onblocked = (event) => {
          console.error('error - db blocked.', event)
          blockedPopup()
        }

        DBOpenRequest.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result
          const upgradeTransaction = (event.target as IDBOpenDBRequest).transaction

          db.onerror = (error) => {
            console.error('error - db upgrade.', error)
            this.isActive = false

            this.initWaitingReq.forEach((promise) => {
              promise.reject(error)
            })
            this.initWaitingReq = []

            this.isInitialing = false
            reject(error)
          }

          const recreateStores = (migratedAddressBooks: AddressBookData[]): void => {
            if (event.oldVersion != null && event.oldVersion > 0) {
              try {
                db.deleteObjectStore(this.contactTableName)
                db.deleteObjectStore(this.addressBookTableName)
                db.deleteObjectStore(this.photoTableName)
              } catch (e) {
                console.log('error - db table delete failed.', e)
              }
            }

            const contactObjectStore = db.createObjectStore(this.contactTableName, {
              keyPath: 'serverId',
            })
            contactObjectStore.createIndex('emails', 'emails', { unique: false })
            contactObjectStore.createIndex('phones', 'phones', { unique: false })
            contactObjectStore.createIndex('allValues', 'allValues', { unique: false })

            db.createObjectStore(this.photoTableName, {
              keyPath: 'serverId',
            })

            const addressBookObjectStore = db.createObjectStore(this.addressBookTableName, {
              keyPath: 'url',
            })

            migratedAddressBooks.forEach((record) => {
              addressBookObjectStore.put(record)
            })
          }

          // addressBook 은 store 를 지우기 전에 기존 레코드를 먼저 읽어 path 기반으로
          // 이전해야 isSync 사용자 설정이 유지된다. (contact/photo 캐시는 그대로 폐기한다.)
          const hasExistingAddressBookStore =
            event.oldVersion != null &&
            event.oldVersion > 0 &&
            db.objectStoreNames.contains(this.addressBookTableName)

          if (hasExistingAddressBookStore && upgradeTransaction) {
            const oldRecords: AddressBookData[] = []
            const cursorRequest = upgradeTransaction
              .objectStore(this.addressBookTableName)
              .openCursor()

            cursorRequest.onsuccess = (cursorEvent) => {
              const cursor = (cursorEvent.target as IDBRequest<IDBCursorWithValue>).result
              if (cursor) {
                oldRecords.push(cursor.value)
                cursor.continue()
              } else {
                recreateStores(migrateAddressBookRecordsToPath(oldRecords))
              }
            }

            cursorRequest.onerror = (error) => {
              console.log('error - failed to read old addressBook records for migration.', error)
              recreateStores([])
            }
          } else {
            recreateStores([])
          }
        }
      } else {
        this.initWaitingReq.push({
          resolve,
          reject,
        })
      }
    })
  }

  close(): void {
    if (this.isActive === true && this.db) {
      this.db.close()
    }
    this.isActive = false
  }

  addData(
    tableName: TableName,
    data: ContactData | AddressBookData | PhotoData,
  ): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.add(data)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Added successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  putData(
    tableName: TableName,
    data: ContactData | AddressBookData | PhotoData,
  ): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.put(data)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Added successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to add.', error)
        reject(error)
      }
    })

    return promise
  }

  deleteData(tableName: TableName, key: string): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.delete(key)

      objectStoreRequest.onsuccess = (event) => {
        console.log('Deleted successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to delete.', error)
        reject(error)
      }
    })

    return promise
  }

  clear(tableName: TableName): Promise<boolean | null> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise<boolean>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readwrite')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.clear()

      objectStoreRequest.onsuccess = (event) => {
        console.log('cleared successfully.', event)
        resolve(true)
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to clear.', error)
        reject(error)
      }
    })

    return promise
  }

  getData(tableName: TableName, key: string): Promise<any> {
    if (this.isActive !== true) {
      return Promise.resolve(null)
    }

    const promise = new Promise((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(tableName)

      const objectStoreRequest = objectStore.get(key)

      objectStoreRequest.onsuccess = (/* event */) => {
        const { result } = objectStoreRequest

        if (result != null) {
          resolve(result)
        } else {
          console.log('No matching data.', tableName, key)
          resolve(null)
        }
      }

      objectStoreRequest.onerror = (error) => {
        console.log('failed to get.', error)
        reject(error)
      }
    })

    return promise
  }

  getAllData(tableName: TableName): Promise<any[]> {
    if (this.isActive !== true) {
      return Promise.resolve([])
    }

    const promise = new Promise<any[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction([tableName], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore(tableName)

      const cursorRequest = objectStore.openCursor()

      const dataList: any[] = []
      cursorRequest.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result
        if (cursor) {
          dataList.push(cursor.value)
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }

      cursorRequest.onerror = (error) => {
        console.log('failed to getAll.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContact(searchText: string | null): Promise<ContactData[] | null> {
    if (this.isActive !== true || searchText == null) {
      return Promise.resolve(null)
    }

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('allValues')

      const dataList: ContactData[] = []
      const searchTextLower = searchText.toLowerCase()

      const request = index.openCursor()
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor) {
          if ((cursor.key as string).includes(searchTextLower)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactByEmail(email: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || email == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('emails')

      const targetEmail = email.toLowerCase() + '\n'
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetEmail)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactNameEmail(text: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || text == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('emails')

      const targetText = text.toLowerCase()
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetText)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  searchContactByPhone(phone: string | null, count: number = -1): Promise<ContactData[] | null> {
    if (this.isActive !== true || phone == null) {
      return Promise.resolve(null)
    }

    const inCount = count <= 0 ? Number.MAX_SAFE_INTEGER : count

    const promise = new Promise<ContactData[]>((resolve, reject) => {
      if (!this.db) {
        reject(new Error('Database not initialized'))
        return
      }

      const transaction = this.db.transaction(['contact'], 'readonly')

      transaction.onerror = (error) => {
        console.log('Transaction not opened due to error. Duplicate items not allowed.', error)
        reject(error)
      }

      const objectStore = transaction.objectStore('contact')
      const index = objectStore.index('phones')

      const targetPhone = phone.toLowerCase()
      const request = index.openCursor()
      const dataList: ContactData[] = []
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest<IDBCursorWithValue>).result

        if (cursor && dataList.length < inCount) {
          if ((cursor.key as string).includes(targetPhone)) {
            const v = cursor.value
            dataList.push({
              serverId: v.serverId,
              etag: v.etag,
              contact: v.contact,
              emails: v.emails,
              phones: v.phones,
              allValues: v.allValues,
            })
          }
          cursor.continue()
        } else {
          resolve(dataList)
        }
      }
      request.onerror = (error) => {
        console.log('failed to search.', error)
        reject(error)
      }
    })

    return promise
  }

  async getDB(): Promise<boolean> {
    if (this.isActive === true) {
      return Promise.resolve(true)
    }
    const initRes = await this.init()
    return initRes
  }
}

export default PersonalContactDB
